<app-vault-module-shell
  moduleLabel="Bóveda Digital"
  [accessDenied]="accessDenied()"
  [loadError]="loadError()"
  [loadErrorTitle]="loadErrorTitle()"
>
<section class="repository" [class.repository--explorer]="viewMode() === 'explorer'">
  <app-page-header
    title="Bóveda Digital"
    subtitle="Navega, busca, previsualiza y descarga documentos del repositorio."
    icon="Vault"
    [actions]="pageHeaderActions()"
    (actionClick)="onPageHeaderAction($event)"
  >
    @if (canAssignFiles()) {
      <span header-actions class="repository__header-assign" title="Asignaciones">
        <lib-button
          variant="icon-ghost"
          prefixIcon="UserCheck"
          [iconSize]="20"
          ariaLabel="Asignaciones — gestión de asignaciones"
          (clicked)="openAssignmentsDialog()"
        />
      </span>
    }
  </app-page-header>

    @if (loading()) {
      <div class="loading-container">
        <lib-empty message="Cargando repositorio" description="Espere por favor..." />
      </div>
    }
    @if (error()) {
      <app-vault-page-alert
        type="error"
        title="Error"
        [message]="error()"
        (closed)="error.set(null)"
      />
    }
    @if (info()) {
      <app-vault-page-alert
        type="warning"
        title="Aviso"
        [message]="info()"
        (closed)="info.set(null)"
      />
    }

    @if (viewMode() === 'table' && !currentZipPath()) {
      <fieldset class="repository__fieldset">
        <legend>Búsqueda y ubicación</legend>
        <div class="repository__search-grid">
          <app-field-label
            class="repository__search-label repository__search-label--query"
            label="Búsqueda global"
            tooltip="Busca por nombre de archivo (min. 2 caracteres). Si solo eliges tipo PDF/ZIP/etc., deja el nombre vacío y pulsa Buscar."
            />
          <app-field-label
            class="repository__search-label repository__search-label--type"
            label="Tipo de archivo"
            tooltip="Acota por extensión (pdf, zip, imagen…). Puede usarse solo, sin texto en búsqueda global."
            />
          <app-field-label
            class="repository__search-label repository__search-label--zip"
            label="Incluir contenido dentro de ZIP"
            tooltip="Si está marcado, busca también dentro de ZIP/RAR (por nombre o tipo). Si el API no indexa ZIPs, el front recorre los ZIP de la carpeta actual."
            />
          <span class="repository__search-label repository__search-label--actions" aria-hidden="true"></span>

          <div class="repository__search-control repository__search-control--query">
            <lib-input
              label=""
              placeholder="Ej: contrato o vacío + tipo PDF/ZIP"
              [value]="searchTerm()"
              (valueChange)="onSearchTermChange($event)"
              />
          </div>

          <div class="repository__search-control repository__search-control--type">
            <lib-select
              label=""
              [options]="fileTypeOptions"
              [value]="fileTypeFilter()"
              (valueChange)="onFileTypeFilterChange($event)"
              />
          </div>

          <div class="repository__search-control repository__search-control--zip">
            <lib-checkbox
              label=""
              [checked]="searchInZips()"
              (checkedChange)="onSearchInZipsChange($event)"
              />
          </div>

          <div class="repository__search-actions">
            <lib-button
              variant="primary"
              prefixIcon="Search"
              [disabled]="loading()"
              [loading]="loading()"
              (clicked)="applySearch()"
            >
              Buscar
            </lib-button>
            @if (repositorySearchActive() || hasRepositoryFilterUiState()) {
              <lib-button
                variant="secondary"
                prefixIcon="X"
                [disabled]="loading()"
                (clicked)="restoreRoot()"
              >
                Limpiar filtros
              </lib-button>
            }
          </div>
        </div>
      </fieldset>
    }

    @if (viewMode() === 'table') {
      <div class="repository__table-wrap">
        <div class="repository__table-head">
          @if (showAssignmentEntrySelector()) {
            <div class="repository__assignment-selector">
              <app-field-label label="Mis asignaciones" />
              <lib-select
                label=""
                [options]="assignmentEntryOptions()"
                [value]="activeAssignmentEntryPath()"
                (valueChange)="onActiveAssignmentEntryChange($event)"
              />
            </div>
          }
          <p class="repository__path" [class.repository__path--search]="repositorySearchActive()">
            <strong>Contexto:</strong>
            {{ tableContextLabel() }}
          </p>
          <div class="repository__table-nav">
            <lib-button
              variant="primary"
              prefixIcon="RefreshCcw"
              [disabled]="loading()"
              (clicked)="restoreRoot()"
            >
              Restaurar
            </lib-button>
            <lib-button
              variant="secondary"
              [prefixIcon]="repositorySearchActive() ? 'X' : 'ArrowLeft'"
              [disabled]="!canTableNavBack()"
              (clicked)="onTableNavBack()"
            >
              {{ repositorySearchActive() ? 'Salir de búsqueda' : 'Volver' }}
            </lib-button>
            @if (currentZipPath()) {
              <lib-button
                variant="secondary"
                prefixIcon="X"
                [disabled]="!currentZipPath()"
                (clicked)="leaveZip()"
              >
                Salir ZIP
              </lib-button>
            }
          </div>
        </div>

        @if (rows().length > 0) {
          <lib-table
            [config]="tableConfig()"
            [data]="rows()"
            [loading]="loading()"
            [pageSize]="tablePageSize()"
            [pageSizeOptions]="tablePageSizeOptions"
            (pageChange)="onTablePageChange($event)"
            [sortColumn]="tableSortColumn()"
            [sortDirection]="tableSortDirection()"
            (sortChange)="onTableSort($event)"
            (rowClick)="onRowClick($event)"
            (action)="onAction($event)"
            >
            <ng-template #cellTemplate let-row let-column="column">
              @if (column.key === 'name') {
                <div class="repository__name-cell">
                  <span>{{ displayRepositoryItemNameWithVersion(row) }}</span>
                </div>
              } @else if (column.key === 'type') {
                <span>{{ displayRepositoryItemType(row) }}</span>
              } @else if (column.key === 'size') {
                <span>{{ displayRepositoryItemSize(row) }}</span>
              }
            </ng-template>
          </lib-table>
        } @else if (!loading() && !error()) {
          <lib-empty
            icon="FolderSearch"
            message="Sin resultados"
            [description]="repositoryListEmptyDescription()"
          />
          <div class="vault-empty-cta">
            <lib-button variant="secondary" type="button" prefixIcon="RefreshCcw" (clicked)="restoreRoot()">
              Actualizar
            </lib-button>
          </div>
        }
      </div>
    }

    @if (viewMode() === 'explorer') {
      <div class="repository__explorer">
        @if (canAssignFiles() && explorerAssignableFolder(); as folder) {
          <div class="repository__explorer-toolbar">
            <p class="repository__explorer-toolbar-label">
              Carpeta seleccionada: <strong>{{ folder.name }}</strong>
            </p>
            <lib-button
              variant="secondary"
              prefixIcon="UserCheck"
              (clicked)="openAssignExplorerFolder()"
            >
              Asignar carpeta
            </lib-button>
          </div>
        }
        <app-file-tree
          [showFilePreview]="true"
          [canAssign]="canAssignFiles()"
          [assignablePathPredicate]="canAssignRepositoryItemFn"
          [virtualFolderPreview]="explorerVirtualFolder()"
          [fileTreeData]="explorerFileTree()"
          [flatSearchResults]="repositorySearchActive()"
          [globalFileSearch]="true"
          [externalSearchTerm]="searchTerm()"
          [searchLoading]="loading()"
          [selectedPathExternal]="selectedExplorerPath()"
          [selectedFilePathExternal]="selectedExplorerFilePath()"
          [useExternalExpand]="repositorySearchActive() ? false : true"
          (searchChanged)="onExplorerTreeSearchChange($event)"
          (searchSubmitted)="onExplorerTreeSearchSubmit($event)"
          (treeExpandAction)="onExplorerTreeExpandAction($event)"
          (folderSelected)="onExplorerFolderSelected($event)"
          (fileSelected)="onExplorerFileSelected($event)"
          (fileDownloaded)="onExplorerFileDownloaded($event)"
          (assignFile)="onExplorerAssignFile($event)"
          (exitVirtualFolder)="onExplorerExitVirtualFolder()"
        />
      </div>
    }

    @if (assignDialogOpen()) {
      <div
        class="repository__backdrop"
        (click)="closeAssignDialog()"
        (keydown.escape)="closeAssignDialog()"
        tabindex="-1"
        role="presentation"
      ></div>
      <div
        class="repository__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="repository-assign-title"
        (click)="$event.stopPropagation()"
      >
        <header class="repository__dialog-head">
          <div class="repository__dialog-title-row">
            <lib-icon name="UserPlus" [size]="20" aria-hidden="true" />
            <h2 id="repository-assign-title">{{ assignDialogTitle() }}</h2>
          </div>
          <p class="repository__dialog-desc">
            Asigna estos archivos o carpetas a usuarios de la empresa. Cada usuario ya tiene su rol operativo en Vault.
          </p>
        </header>

        <div class="repository__dialog-body">
          <div class="repository__assign-section">
            <app-field-label
              [label]="
                assignTargetItems().length > 1
                  ? assignTargetItems().length + ' elementos seleccionados'
                  : assignTargetItems()[0]?.isDirectory
                    ? 'Carpeta'
                    : 'Archivo'
              "
            />
            <ul class="repository__assign-items">
              @for (item of assignTargetItems(); track item.path) {
                <li class="repository__assign-item">
                  <div class="repository__assign-item-main">
                    <span class="repository__assign-item-name">{{ item.name }}</span>
                    @if (isSuperAdmin()) {
                      <span class="repository__assign-item-path">{{ item.path }}</span>
                    } @else {
                      <span class="repository__assign-item-path">{{ getAssignLocationHint(item) }}</span>
                    }
                  </div>
                  @if (assignTargetItems().length > 1) {
                    <lib-button
                      variant="icon-ghost"
                      prefixIcon="X"
                      [iconSize]="16"
                      ariaLabel="Quitar de la selección"
                      type="button"
                      (clicked)="removeAssignTargetItem(item.path)"
                    />
                  }
                </li>
              }
            </ul>
            @if (assignShowsOaisHint()) {
              <div class="repository__assign-sip-hint" role="note">
                <p class="repository__assign-sip-title">Norma OAIS (DIP)</p>
                <p class="repository__muted">
                  Solo se pueden asignar elementos bajo DIP (difusión). El contenido SIP/AIP no es asignable a consulta.
                </p>
              </div>
            }
          </div>

          <div class="repository__assign-section">
            <app-field-label label="Asignar a usuarios" />
            @if (assignUsersLoading()) {
              <p class="repository__muted">Cargando usuarios...</p>
            } @else {
              <lib-select
                label=""
                placeholder="Seleccione usuario(s)..."
                [options]="assignUserSelectOptions()"
                [value]="assignUserPicker()"
                [disabled]="assignUserSelectOptions().length === 0"
                (valueChange)="onAssignUserPickerChange($event)"
              />
              @if (assignUsersLoadHint()) {
                <p class="repository__muted">{{ assignUsersLoadHint() }}</p>
              } @else if (assignUserSelectOptions().length === 0 && selectedAssignUsers().length === 0) {
                <p class="repository__muted">No hay más usuarios disponibles para agregar.</p>
              }
              @if (selectedAssignUsers().length > 0) {
                <p class="repository__muted">
                  {{ selectedAssignUsers().length }} usuario(s) seleccionado(s)
                </p>
                <ul class="repository__assign-selected">
                  @for (user of selectedAssignUsers(); track user.id) {
                    <li class="repository__assign-selected-item">
                      <span>{{ formatAssignUserLabel(user) }}</span>
                      <lib-button
                        variant="icon-ghost"
                        prefixIcon="X"
                        [iconSize]="16"
                        ariaLabel="Quitar usuario"
                        type="button"
                        (clicked)="removeSelectedAssignUser(user.id)"
                      />
                    </li>
                  }
                </ul>
              }
            }
          </div>

          <div class="repository__assign-section">
            <app-field-label label="Tiempo de expiración" />
            <lib-select
              label=""
              [options]="assignExpirationOptions"
              [value]="assignExpiration()"
              (valueChange)="onAssignExpirationChange($event)"
            />
          </div>
        </div>

        <footer class="repository__dialog-actions repository__dialog-actions--legacy">
          <lib-button variant="secondary" (clicked)="closeAssignDialog()">Cancelar</lib-button>
          <lib-button
            variant="primary"
            prefixIcon="UserPlus"
            [loading]="assignSaving()"
            [disabled]="!canSubmitAssign()"
            (clicked)="saveAssignment()"
          >
            Asignar
          </lib-button>
        </footer>
      </div>
    }

    @if (assignmentsDialogOpen()) {
      <div
        class="repository__backdrop"
        (click)="closeAssignmentsDialog()"
        (keydown.escape)="closeAssignmentsDialog()"
        tabindex="-1"
        role="presentation"
      ></div>
      <div
        class="repository__dialog repository__dialog--wide"
        role="dialog"
        aria-modal="true"
        aria-labelledby="repository-assignments-title"
        (click)="$event.stopPropagation()"
      >
        <header class="repository__dialog-head repository__dialog-head--toolbar">
          <div class="repository__dialog-head-main">
            <div class="repository__dialog-title-row">
              <lib-icon name="Settings" [size]="20" aria-hidden="true" />
              <h2 id="repository-assignments-title">Gestión de Asignaciones</h2>
            </div>
          </div>
          <lib-button
            variant="icon-ghost"
            prefixIcon="X"
            [iconSize]="18"
            ariaLabel="Cerrar"
            type="button"
            (clicked)="closeAssignmentsDialog()"
          />
        </header>

        <div class="repository__dialog-body repository__dialog-body--assignments">
          @if (assignmentsLoading()) {
            <p class="repository__muted repository__assignments-empty">Cargando asignaciones...</p>
          } @else if (assignmentGroups().length === 0) {
            <p class="repository__muted repository__assignments-empty">No hay asignaciones registradas.</p>
          } @else {
            <div class="repository__assignments-toolbar">
              <div class="repository__assignments-toolbar-left">
                <span class="repository__dialog-btn-sm">
                  <lib-button variant="secondary" prefixIcon="SquareCheck" (clicked)="selectAllAssignments()">
                    Seleccionar todo
                  </lib-button>
                </span>
                <span class="repository__dialog-btn-sm">
                  <lib-button
                    variant="secondary"
                    prefixIcon="X"
                    [disabled]="selectedAssignmentCount() === 0"
                    (clicked)="clearAssignmentSelection()"
                  >
                    Limpiar selección
                  </lib-button>
                </span>
                @if (selectedAssignmentCount() > 0) {
                  <span class="repository__assignments-selected-count">
                    {{ selectedAssignmentCount() }} elemento(s) seleccionado(s)
                  </span>
                }
              </div>
              <div class="repository__assignments-toolbar-right">
                <span class="repository__dialog-btn-sm repository__btn-danger">
                  <lib-button
                    variant="secondary"
                    prefixIcon="UserMinus"
                    [disabled]="selectedAssignmentCount() === 0 || assignmentsUnassigning()"
                    [loading]="assignmentsUnassigning()"
                    (clicked)="unassignSelectedAssignments()"
                  >
                    Desasignar seleccionados
                  </lib-button>
                </span>
                <span class="repository__dialog-btn-sm repository__btn-danger">
                  <lib-button
                    variant="secondary"
                    prefixIcon="Trash2"
                    [disabled]="assignmentGroups().length === 0 || assignmentsUnassigning()"
                    [loading]="assignmentsUnassigning()"
                    (clicked)="unassignAllAssignments()"
                  >
                    Desasignar todo
                  </lib-button>
                </span>
              </div>
            </div>

            <ul class="repository__assignments-list">
              @for (group of assignmentGroups(); track group.id) {
                <li class="repository__assignments-item">
                  <lib-checkbox
                    [label]="''"
                    [checked]="isAssignmentGroupSelected(group)"
                    (checkedChange)="toggleAssignmentGroup(group, $event)"
                  />
                  <div class="repository__assignments-item-body">
                    <div class="repository__assignments-item-head">
                      <span class="repository__assign-item-name">
                        @if (group.isDirectory) {
                          <lib-icon name="Folder" [size]="16" class="repository__assignments-type-icon" />
                        } @else {
                          <lib-icon name="File" [size]="16" class="repository__assignments-type-icon" />
                        }
                        {{ group.fileName }}
                      </span>
                      <span
                        class="repository__assignments-expiry"
                        [class.repository__assignments-expiry--expired]="group.isExpired"
                      >
                        {{ assignmentExpiryLabel(group) }}
                      </span>
                      @if (!group.oaisCompliant) {
                        <lib-badge text="No conforme OAIS" variant="warning" size="small" />
                      }
                    </div>
                    <span class="repository__assign-item-path">{{ group.filePath }}</span>
                    @if (group.users.length > 0) {
                      <div class="repository__assignments-users">
                        @for (user of group.users; track user.id) {
                          <span class="repository__assignments-user">
                            {{ formatAssignUserLabel(user) }}
                          </span>
                        }
                      </div>
                    }
                    @if (group.roles.length > 0) {
                      <div class="repository__assignments-roles">
                        @for (role of group.roles; track role) {
                          <span class="repository__assignments-role">{{ formatAssignmentRoleLabel(role) }}</span>
                        }
                      </div>
                    }
                  </div>
                  <span class="repository__dialog-btn-sm">
                    <lib-button
                      variant="secondary"
                      prefixIcon="UserMinus"
                      type="button"
                      [disabled]="assignmentsUnassigning()"
                      (clicked)="unassignAssignmentGroup(group)"
                    >
                      Desasignar
                    </lib-button>
                  </span>
                </li>
              }
            </ul>
          }
        </div>
      </div>
    }

    @if (previewOpen()) {
      <app-vault-file-preview
        [title]="previewTitle()"
        [mimeType]="previewMimeType()"
        [srcUrl]="previewSrcUrl()"
        [srcData]="previewSrcData()"
        [textContent]="previewText()"
        [unsupportedMessage]="previewUnsupportedMessage()"
        [loading]="previewLoading()"
        [errorMessage]="previewError()"
        [showDownloadAction]="!!(previewUnsupportedMessage() && previewSrcUrl())"
        [downloadLabel]="'Descargar archivo'"
        (downloadFile)="downloadPreviewFile()"
        (closePreview)="closePreview()"
        />
    }
  </section>
</app-vault-module-shell>
